import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { BackdropLoader, Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { Button, Checkbox, MenuItem, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import DropFileUploader from 'src/components/shared/DropFileUploader';
import { useStore } from 'src/stores';
import useStyles from './uploadBillModalStyles';

/** Placeholder produced by generateAddressString when a property has no address; not worth sending for matching. */
const DEFAULT_ADDRESS = 'Default Address';
const getPropertyLabel = property => property.propertyAddress || property.propertyName || property.propertyId;
const getUtilityTypes = property => {
  var _property$utilities;
  return [...new Set(((_property$utilities = property.utilities) !== null && _property$utilities !== void 0 ? _property$utilities : []).map(utility => utility.utilityType).filter(Boolean))];
};
const getUtilitiesLabel = property => {
  const utilityTypes = getUtilityTypes(property);
  return utilityTypes.length ? utilityTypes.join(', ') : 'No utilities configured';
};

/** Picks the properties a bill covers and the bill file, then starts extraction for each property. */
export const UploadBillModal = observer(_ref => {
  let isOpen = _ref.isOpen,
    handleClose = _ref.handleClose;
  const classes = useStyles();
  const store = useStore();
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    selectedPropertyIds = _useState2[0],
    setSelectedPropertyIds = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    searchKey = _useState4[0],
    setSearchKey = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    file = _useState6[0],
    setFile = _useState6[1];
  // Remounts the uploader so picking the same file twice still fires a change event.
  const _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    uploaderKey = _useState8[0],
    setUploaderKey = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    startedCount = _useState0[0],
    setStartedCount = _useState0[1];
  const properties = store.company.propertiesList;
  const propertiesAsync = store.company.getCompanyPropertiesAsync;
  useEffect(() => {
    if (isOpen) return;
    setSelectedPropertyIds([]);
    setSearchKey('');
    setFile(null);
    setUploaderKey(key => key + 1);
    setStartedCount(null);
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || properties.length > 0 || propertiesAsync.inProgress || propertiesAsync.hasEverBeenRun) return;
    propertiesAsync.run({}, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const search = searchKey.trim().toLowerCase();
  const visibleProperties = properties.slice().sort((a, b) => getPropertyLabel(a).localeCompare(getPropertyLabel(b), undefined, {
    numeric: true
  })).filter(property => search ? "".concat(getPropertyLabel(property), " ").concat(getUtilitiesLabel(property)).toLowerCase().includes(search) : true);
  const isAllVisibleSelected = visibleProperties.length > 0 && visibleProperties.every(property => selectedPropertyIds.includes(property.propertyId));
  const toggleProperty = propertyId => {
    setSelectedPropertyIds(prev => prev.includes(propertyId) ? prev.filter(id => id !== propertyId) : [...prev, propertyId]);
  };
  const toggleAllVisible = () => {
    const visibleIds = visibleProperties.map(property => property.propertyId);
    setSelectedPropertyIds(prev => isAllVisibleSelected ? prev.filter(id => !visibleIds.includes(id)) : [...new Set([...prev, ...visibleIds])]);
  };
  const handleFileChange = value => {
    setFile(value instanceof File ? value : null);
  };
  const isSubmitting = startedCount !== null;
  const handleUpload = async () => {
    const selectedProperties = properties.filter(property => selectedPropertyIds.includes(property.propertyId));
    const failedPropertyIds = [];
    let started = 0;

    // The file itself is never stored server side for this flow, so keep a session URL for previews.
    const fileUrl = URL.createObjectURL(file);
    const fileSizeMB = file.size / 1048576;
    setStartedCount(0);

    // Sequential on purpose: asyncModel.run cancels the pending request of the same action.
    for (const property of selectedProperties) {
      var _resp$response;
      const utilityTypes = getUtilityTypes(property);
      const propertyAddress = property.propertyAddress === DEFAULT_ADDRESS ? '' : property.propertyAddress;

      // eslint-disable-next-line no-await-in-loop
      const resp = await store.billAutomationBillsApi.startBillAsync.run({
        file,
        propertyId: property.propertyId,
        propertyAddress: propertyAddress || undefined,
        requiredUtilities: utilityTypes.length ? utilityTypes : undefined
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful && (_resp$response = resp.response) !== null && _resp$response !== void 0 && _resp$response.billId) {
        store.uploadedBills.addUploadedBill(_objectSpread(_objectSpread({}, resp.response), {}, {
          propertyId: property.propertyId,
          propertyAddress: getPropertyLabel(property),
          fileName: file.name,
          fileUrl,
          fileSizeMB,
          requestedUtilityTypes: utilityTypes
        }));
        started += 1;
        setStartedCount(started);
      } else {
        failedPropertyIds.push(property.propertyId);
      }
    }
    setStartedCount(null);
    if (started === 0) URL.revokeObjectURL(fileUrl);
    if (started > 0) {
      toast('success', {
        body: ["Bill uploaded for ".concat(started, " propert").concat(started === 1 ? 'y' : 'ies', ". Extraction is in progress.")]
      });
    }
    if (failedPropertyIds.length === 0) {
      handleClose();
      return;
    }

    // Each failure already raised its own toast, so keep the leftovers selected for a retry.
    setSelectedPropertyIds(failedPropertyIds);
  };
  const subTitle = properties.length ? "".concat(selectedPropertyIds.length, " of ").concat(properties.length, " propert").concat(properties.length === 1 ? 'y' : 'ies', " selected") : '';
  return /*#__PURE__*/React.createElement(SimpleModal, {
    title: "Upload bill",
    subTitle: subTitle,
    isOpen: isOpen,
    handleClose: handleClose
    // Without this the modal rebuilds its children on every render and the search field loses focus.
    ,
    isMemoChildren: true,
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleUpload,
      disabled: selectedPropertyIds.length === 0 || !file || isSubmitting,
      'custom-attribute': 'bills-upload-bill-modal-submit'
    }, isSubmitting && selectedPropertyIds.length > 1 ? "Uploading ".concat(Math.min(startedCount + 1, selectedPropertyIds.length), " of ").concat(selectedPropertyIds.length, "\u2026") : 'Upload bill')
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isSubmitting
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4",
    className: classes.sectionTitle
  }, "Select properties"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.sectionHint
  }, "Pick every property this bill covers."), properties.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.emptyState
  }, propertiesAsync.inProgress ? 'Loading properties…' : 'No properties are available for this company yet.') : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextField, {
    id: "upload_bill_search_property",
    name: "upload_bill_search_property",
    label: "Search for property",
    variant: "outlined",
    className: classes.searchField,
    value: searchKey,
    onChange: event => setSearchKey(event.target.value),
    fullWidth: true
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.propertiesList
  }, visibleProperties.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.emptyState
  }, "No properties match your search.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuItem, {
    variant: "multiple",
    className: classes.selectAllItem,
    onClick: toggleAllVisible,
    'custom-attribute': 'bills-upload-bill-modal-select-all'
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: isAllVisibleSelected
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, isAllVisibleSelected ? 'Deselect all' : 'Select all')), visibleProperties.map(property => /*#__PURE__*/React.createElement(MenuItem, {
    key: property.propertyId,
    value: property.propertyId,
    variant: "multiple",
    className: classes.propertyItem,
    selected: selectedPropertyIds.includes(property.propertyId),
    onClick: () => toggleProperty(property.propertyId)
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: selectedPropertyIds.includes(property.propertyId)
  }), /*#__PURE__*/React.createElement(Icon, {
    type: "folder-house"
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.propertyItemText
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, getPropertyLabel(property)), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.propertyUtilities
  }, getUtilitiesLabel(property))))))))), /*#__PURE__*/React.createElement("div", {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4",
    className: classes.sectionTitle
  }, "Attach bill file"), /*#__PURE__*/React.createElement(DropFileUploader, {
    key: "upload-bill-file-".concat(uploaderKey),
    callback: handleFileChange,
    fileName: (file === null || file === void 0 ? void 0 : file.name) || '',
    noPreview: true
  })));
});